"use client";

import { useEffect, useRef, useState } from "react";
import { Loader2, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/use-i18n";
import { toast } from "sonner";
export function ContactForm() {
  const t = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 2026-09-23 FASE 4 MEJORA transversal oferta 9/9 M5 (research 2026 W3C
  // WCAG22 Understanding 3.3.1 updated 12 June 2026 + técnica ARIA21:
  // aria-invalid + aria-describedby juntos asocian el error al campo; el SR
  // anuncia qué corregir. Paridad SuggestionForm M1 / NewsletterSignup: el
  // email inválido por regex local es error POR CAMPO (marca el campo); el
  // error global de red/servidor sigue sin marcar campos. Sin cambio visual,
  // sin keys nuevas — reusa newsletterInvalidEmail existente en 14).
  const [emailInvalid, setEmailInvalid] = useState(false);
  const errorRef = useRef<HTMLParagraphElement>(null);

  // P2 a11y: al aparecer el error, mover foco a él para lectores de pantalla.
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setEmailInvalid(false);

    // Honeypot: bots fill it — fake success without hitting the API
    // (mismo patrón que SuggestionForm/NewsletterSignup).
    if (honeypot) {
      toast.success(t("contactSuccess"), {
        icon: <CheckCircle className="size-4 text-[var(--success)]" />,
        duration: 5000
      });
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      setHoneypot("");
      return;
    }
    setLoading(true);
    try {
      // Normaliza como Zod (.trim().toLowerCase().email()) + server schema —
      // con espacios el servidor daría 400 tras el roundtrip; el regex local
      // lo frena antes (paridad SuggestionForm).
      const normalizedEmail = email.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
        setEmailInvalid(true);
        setError(t("newsletterInvalidEmail"));
        setLoading(false);
        return;
      }
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name,
          email: normalizedEmail,
          subject,
          message,
          honeypot
        })
      });
      const data = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      if (!response.ok) {
        setError(data.error ?? t("contactErrorSend"));
        return;
      }
      toast.success(t("contactSuccess"), {
        icon: <CheckCircle className="size-4 text-[var(--success)]" />,
        duration: 5000
      });
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      setHoneypot("");
    } catch (err) {
      // 2026-09-23 FASE 4 transversal M4 (research 2026 OWASP Logging Cheat
      // Sheet + GDPR art.5(1)(c): err crudo puede arrastrar PII del formulario
      // — solo mensaje truncado 120 chars en dev, nunca email/message).
      if (process.env.NODE_ENV !== "production") {
        const safeMsg = err instanceof Error ? err.message.slice(0, 120) : "unknown";
        console.error("[ContactForm] Submit failed:", safeMsg);
      }
      setError(t("contactErrorConnection"));
      toast.error(t("contactErrorConnection"));
    } finally {
      setLoading(false);
    }
  };
  return <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot anti-spam: hidden from users, bots fill it. Validated server-side (max 0). */}
      <input type="text" name="honeypot" value={honeypot} onChange={event_0 => setHoneypot(event_0.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="contact-name" className="text-sm font-medium tracking-tight">
            {t("contactNameLabel")}
          </label>
          <Input id="contact-name" name="name" type="text" autoComplete="name" required minLength={2} maxLength={80} value={name} onChange={event_1 => setName(event_1.target.value)}
        // 2026-09-23 FASE 3 FIX diseño (research 2026 M3 Expressive + Fluent shapes:
        // fields 16px --radius-field SSOT, pill 9999 solo botones — globals.css:116).
        className="h-12 rounded-[var(--radius-field)] border-border/60 bg-card px-4 sm:text-[15px]" />
        </div>
        <div className="space-y-2">
          <label htmlFor="contact-email" className="text-sm font-medium tracking-tight">
            {t("contactEmailLabel")}
          </label>
          <Input id="contact-email" name="email" type="email" autoComplete="email" required maxLength={160} value={email} onChange={event_2 => {
          setEmail(event_2.target.value);
          if (emailInvalid) setEmailInvalid(false);
        }} aria-invalid={emailInvalid ? true : undefined} aria-describedby={emailInvalid || error ? "contact-error" : undefined} className="h-12 rounded-[var(--radius-field)] border-border/60 bg-card px-4 sm:text-[15px]" />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="contact-subject" className="text-sm font-medium tracking-tight">
          {t("contactSubjectLabel")}
        </label>
        <Input id="contact-subject" name="subject" type="text" required minLength={3} maxLength={120} value={subject} onChange={event_3 => setSubject(event_3.target.value)} className="h-12 rounded-[var(--radius-field)] border-border/60 bg-card px-4 sm:text-[15px]" />
      </div>

      <div className="space-y-2">
        <label htmlFor="contact-message" className="text-sm font-medium tracking-tight">
          {t("contactMessageLabel")}
        </label>
        <Textarea id="contact-message" name="message" required minLength={10} maxLength={2000} rows={6} value={message} onChange={event_4 => setMessage(event_4.target.value)}
      // 2026-09-25 RUN1 FASE3-FIX (SEED=2026-09-25-c6c873bf): resize-none →
      // resize-y, paridad SuggestionForm (el usuario redimensiona vertical).
      className="min-h-[140px] w-full resize-y rounded-[var(--radius-panel)] border-border/60 shadow-sm text-pretty" />
      </div>

      {error ?
    // 2026-09-21 FASE 3 FIXER a11y (research 2026 WCAG 2.2 3.3.1 Error
    // Identification) + 2026-09-23 FASE 4 M5: el error global
    // (red/servidor) vive en el role=alert separado y SOLO el email con
    // formato inválido (regex local) marca su campo con aria-invalid +
    // describedby — nunca los 4 campos a la vez; la validación
    // required/minLength/type=email restante la hace el navegador.
    <p id="contact-error" ref={errorRef} tabIndex={-1} role="alert" className="text-sm font-medium tracking-tight text-[var(--danger)] outline-none">
          {error}
        </p> : null}

      <Button type="submit" variant="accent" className="btn-shimmer h-12 w-full rounded-[var(--radius-button)] text-[15px] font-semibold tracking-tight shadow-sm" disabled={loading} aria-busy={loading}>
        {loading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Send className="size-4" aria-hidden="true" />}
        {t("contactSubmit")}
      </Button>
    </form>;
}